<template>
    <div class="bg-white rounded-lg shadow p-6">
     
        <fieldset v-if="canImport()" class="flex flex-col mb-6 p-4 bg-gray-50 shadow-sm border border-gray-200">
            <legend>Import Management</legend>
            <div class="flex space-x-4">
                <div class="flex flex-col">
                <div class="relative">
                    <select 
                        id="importType" 
                        v-model="importType"
                        class="w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                    >
                        <option value="">Select Import Type</option>
                        <option value="customers">Customers</option>
                        <option value="employees">Employees</option>
                        <option value="competitors">Competitors</option>
                    </select>
                </div>
                <a
                    v-if="importType"
                    href="#"
                    @click.prevent="downloadSampleCsv"
                    class="text-blue-600 mt-2 hover:text-blue-800 flex justify-center items-center text-sm font-medium mr-4"
                >
                    <inline-svg name="download" class="w-4 h-4 mr-1 text-blue-600 fill-current"></inline-svg>
                    <span class="p-1">Sample File for {{ importType }}</span>
                </a>

                </div>
                
                <button
                    @click="$refs.fileInput.click()" 
                    class="btn btn-sm btn-primary bg-blue-500 hover:bg-blue-600 flex flex-row items-center justify-center max-h-[40px]"
                    :disabled="!importType || uploading"
                    :class="{'opacity-50 cursor-not-allowed': !importType}"
                >
                    <span v-if="uploading">
                        <i class="fas fa-spinner fa-spin mr-2"></i> Uploading...
                    </span>
                    <span v-else class="flex flex-row items-center justify-center">
                        <inline-svg name="upload" classes="w-4 h-4 mr-2 text-white fill-current"></inline-svg>
                        Upload
                    </span>
                </button>
                <input 
                    ref="fileInput"
                    type="file" 
                    accept=".csv" 
                    class="hidden" 
                    @change="handleFileUpload"
                >
            </div>
        </fieldset>

        <div v-if="error" class="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6">
            <p>{{ error }}</p>
        </div>

        <div v-if="success" class="bg-green-100 border-l-4 border-green-500 text-green-700 p-4 mb-6">
            <p>{{ success }}</p>
        </div>

        <!-- Tabs Navigation -->
        <div class="border-b border-gray-200 mb-6">
            <nav class="-mb-px flex space-x-8">
                <button
                    v-for="tab in tabs"
                    :key="tab.id"
                    @click="activeTab = tab.id"
                    class="whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm"
                    :class="activeTab === tab.id 
                        ? 'border-blue-500 text-blue-600' 
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'"
                >
                    {{ tab.name }}
                    <span v-if="tab.badge" class="ml-2 bg-gray-100 text-gray-600 text-xs font-medium px-2 py-0.5 rounded-full">
                        {{ tab.badge }}
                    </span>
                </button>
            </nav>
        </div>
        
        <div class="mt-4" v-if="activeTab === 'imports' || activeTab === 'customers' || activeTab === 'employees' || activeTab === 'competitors'">
            <div class="overflow-x-auto">
                <table class="min-w-full bg-white">
                    <thead>
                        <tr class="bg-gray-100">
                            <th 
                                v-for="column in tableColumns" 
                                :key="column.key" 
                                class="py-3 px-4 text-left"
                                :class="column.class"
                            >
                                {{ column.label }}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr 
                            v-for="(item, index) in activeItems" 
                            :key="item.id || index" 
                            class="border-b hover:bg-gray-50"
                        >
                            <td 
                                v-for="column in tableColumns" 
                                :key="column.key" 
                                class="py-3 px-4"
                                :class="column.class"
                                v-html="formatColumnValue(item, column)"
                            ></td>
                        </tr>
                        <tr v-if="!activeItems.length">
                            <td :colspan="tableColumns.length" class="py-4 px-4 text-center text-gray-500">
                                No data found.
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <!-- Debug: Active Tab: {{ activeTab }}, Pagination: {{ JSON.stringify(pagination[activeTab]) }} -->
            <!-- Unified Pagination Component -->
            <div v-if="pagination[activeTab] && pagination[activeTab].data" class="mt-4">
                <Pagination 
                    :pagination="pagination[activeTab]"
                    @page-changed="(page) => changePage(activeTab, page)"
                />
            </div>

        </div>
    </div>
</template>

<script>
import Pagination from '../common/Pagination.vue';
export default {
    components: {
        Pagination
    },
    props: {
        siteId: {
            type: [String, Number],
            required: true
        },
        initialCustomers: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            activeTab: 'imports',
            importType: '',
            items: [],

            customers: [],
            employees: [],
            competitors: [],
            uploading: false,
            error: null,
            success: null,
            pagination: {
                imports: {
                    current_page: 1,
                    last_page: 1,
                    from: 0,
                    to: 0,
                    total: 0,
                    data: []
                },
                customers: {
                    current_page: 1,
                    last_page: 1,
                    from: 0,
                    to: 0,
                    total: 0,
                    data: []
                },
                employees: {
                    current_page: 1,
                    last_page: 1,
                    from: 0,
                    to: 0,
                    total: 0,
                    data: []
                },
                competitors: {
                    current_page: 1,
                    last_page: 1,
                    from: 0,
                    to: 0,
                    total: 0,
                    data: []
                }
            }
        };
    },
    
    computed: {
        activeItems() {
            // Get data from the pagination object for the active tab
            return this.pagination[this.activeTab]?.data || [];
        },
        tabs() {
            return [
                { id: 'imports', name: 'Import Logs', active: true, count: 0 },
                { id: 'customers', name: 'Customers', count: 0 },
                { id: 'employees', name: 'Employees', count: 0 },
                { id: 'competitors', name: 'Competitors', count: 0 },
            ];
        },
        tableColumns() {
            // Import logs columns
            const importLogsColumns = [
                { 
                    key: 'created_at', 
                    label: 'Date',
                    format: (value) => this.formatDate(value)
                },
                { 
                    key: 'type', 
                    label: 'Type',
                    format: (value) => value ? value.charAt(0).toUpperCase() + value.slice(1) : 'N/A'
                },
                { key: 'file_name', label: 'File' },
                { 
                    key: 'status', 
                    label: 'Status',
                    format: (value) => {
                        const status = value || 'N/A';
                        const classes = this.getStatusBadgeClass(status);
                        return `<span class="px-2 py-1 text-xs font-medium rounded-full ${classes}">${status}</span>`;
                    }
                },
                { 
                    key: 'imported_count', 
                    label: 'Imported',
                    class: 'text-green-600'
                },
                { 
                    key: 'failed_count', 
                    label: 'Failed',
                    class: 'text-red-600'
                },
                {
                    key: 'actions',
                    label: 'Actions',
                    format: (_, row) => {
                        if (row.failed_count > 0) {
                            return `
                                <button 
                                    @click="downloadErrorLog(${row.id})"
                                    class="text-blue-600 hover:text-blue-800 text-sm flex items-center"
                                    title="Download Error Log"
                                >
                                    <i class="fas fa-download mr-1"></i> Errors
                                </button>
                            `;
                        }
                        return '-';
                    }
                }
            ];

            // Customer specific columns
            const customerColumns = [
                { key: 'name', label: 'Name' },
                { key: 'region', label: 'Region' },
                { key: 'address', label: 'Address' },
                { key: 'city', label: 'City' },
                { key: 'state', label: 'State' },
                { key: 'postal_code', label: 'Postal Code' },
                { 
                    key: 'country', 
                    label: 'Country',
                    format: (value) => {
                        if (!value) return 'N/A';
                        return value.name || (typeof value === 'string' ? value : 'N/A');
                    }
                },
                { key: 'latitude', label: 'Latitude' },
                { key: 'longitude', label: 'Longitude' },
                { 
                    key: 'imported_by', 
                    label: 'Imported By',
                    format: (value) => value ? (value.name || value.email || 'N/A') : 'N/A'
                }
            ];

            // Employee specific columns
            const employeeColumns = [
                { key: 'employee_number', label: 'Employee #' },
                { key: 'name', label: 'Name' },
                { key: 'title', label: 'Title' },
                { key: 'job_function', label: 'Job Function' },
                { key: 'solution', label: 'Solution' },
                { key: 'department', label: 'Department' },
                { 
                    key: 'is_management', 
                    label: 'Is Management',
                    format: (value) => value ? 'Yes' : 'No'
                },
                { key: 'pay_grade', label: 'Pay Grade' },
                { key: 'region', label: 'Region' },
                { key: 'address', label: 'Address' },
                { key: 'city', label: 'City' },
                { key: 'state', label: 'State' },
                { key: 'postal_code', label: 'Postal Code' },
                { 
                    key: 'country', 
                    label: 'Country',
                    format: (value) => {
                        if (!value) return 'N/A';
                        return value.name || (typeof value === 'string' ? value : 'N/A');
                    }
                },
                { key: 'latitude', label: 'Latitude' },
                { key: 'longitude', label: 'Longitude' },
                { 
                    key: 'imported_by', 
                    label: 'Imported By',
                    format: (value) => value ? (value.name || value.email || 'N/A') : 'N/A'
                }
            ];

            // Competitor specific columns
            const competitorColumns = [
                { key: 'name', label: 'Name' },
                { key: 'region', label: 'Region' },
                { key: 'address', label: 'Address' },
                { key: 'city', label: 'City' },
                { key: 'state', label: 'State' },
                { key: 'postal_code', label: 'Postal Code' },
                { 
                    key: 'country', 
                    label: 'Country',
                    format: (value) => {
                        if (!value) return 'N/A';
                        return value.name || (typeof value === 'string' ? value : 'N/A');
                    }
                },
                { key: 'latitude', label: 'Latitude' },
                { key: 'longitude', label: 'Longitude' },
                { 
                    key: 'imported_by', 
                    label: 'Imported By',
                    format: (value) => value ? (value.name || value.email || 'N/A') : 'N/A'
                }
            ];

            // Return columns based on active tab
            switch (this.activeTab) {
                case 'employees':
                    return employeeColumns;
                case 'competitors':
                    return competitorColumns;
                case 'customers':
                    return customerColumns;
                case 'imports':
                default:
                    return importLogsColumns;
            }
        }
    },

    methods: {
        canImport() {
            // Allow if user can edit this specific site or has global create permission
            return this.$gate.can('update','site', this.siteId) || this.$gate.can('create','site');
        },
        onFileSelected(event) {
            const file = event.target.files[0];
            if (file) {
                this.selectedFile = file;
                this.handleFileUpload(event); // Automatically trigger upload when file is selected
            }
        },
        handleImportTypeChange(event) {
            console.log('Import type changed to:', this.importType);
            console.log('Event target value:', event.target.value);
        },
        changePage(tab, page) {
            console.log('Changing to page:', page, 'for tab:', tab);
            if (!this.pagination[tab]) {
                console.error('No pagination data for tab:', tab);
                return;
            }
            if (page < 1 || page > this.pagination[tab].last_page) {
                console.log('Invalid page number:', page);
                return;
            }
            this.pagination[tab].current_page = page;
            this.loadTabData(tab);
        },
        
        async loadTabData(tab) {
            console.log('Loading data for tab:', tab);
            if (!this.siteId) {
                console.error('Site ID is not available');
                return;
            }
            
            // Map the tab name to the correct API endpoint
            const endpointMap = {
                'imports': `sites/${this.siteId}/imports`,
                'customers': `sites/${this.siteId}/customers`,
                'employees': `sites/${this.siteId}/employees`,
                'competitors': `sites/${this.siteId}/competitors`
            };
            
            const endpoint = endpointMap[tab] || `sites/${this.siteId}/${tab}`;
            
            try {
                const response = await axios.get(`/${endpoint}`, {
                    params: {
                        page: this.pagination[tab].current_page
                    }
                });
                
                if (response.data) {
                    console.log('Data received for tab:', tab, response.data);
                    
                    // Ensure we have a valid pagination object for this tab
                    if (!this.pagination[tab]) {
                        this.$set(this.pagination, tab, {
                            current_page: 1,
                            last_page: 1,
                            from: 0,
                            to: 0,
                            total: 0,
                            data: []
                        });
                    }
                    
                    // Update the tab's data with the paginated items
                    this[tab] = response.data.data || [];
                    
                    // Update pagination info from the root of the response
                    this.$set(this.pagination, tab, {
                        current_page: response.data.current_page || 1,
                        last_page: response.data.last_page || 1,
                        from: response.data.from || 0,
                        to: response.data.to || 0,
                        total: response.data.total || 0,
                        data: response.data.data || []
                    });
                }
            } catch (error) {
                console.error(`Error loading ${tab}:`, error);
                this.error = `Failed to load ${tab}. Please try again.`;
            }
        },
        
        // Update the tab click handler to load data when switching tabs
        setActiveTab(tab) {
            this.activeTab = tab;
            if (!this.pagination[tab]?.data?.length) {
                this.loadTabData(tab);
            }
        },
        formatColumnValue(item, column) {
            // Handle nested properties (e.g., 'details.total_rows')
            const value = column.key.split('.').reduce((obj, key) => {
                return obj && obj[key] !== undefined ? obj[key] : null;
            }, item);

            // Apply column formatting if specified
            if (column.format) {
                return column.format(value, item);
            }

            // Default formatting
            if (value === null || value === undefined) {
                return '-';
            }

            return value;
        },

        handleFileUpload(event) {
            const file = event.target.files[0];
            if (!file) return;

            this.uploading = true;
            this.error = null;
            this.success = null;

            const formData = new FormData();
            formData.append('file', file);
            formData.append('type', this.importType);
            formData.append('_method', 'POST');

            // Submit the file and get the import log ID
            const endpoint = `/sites/${this.siteId}/import/${this.importType}`;
                
            axios.post(endpoint, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    'X-Requested-With': 'XMLHttpRequest',
                    'Accept': 'application/json'
                }
            })
            .then(response => {
                this.success = response.data.message || 'Import has been queued. Please wait...';
                this.loadTabData(this.activeTab); 
                
                // Start polling for import status
                const importLogId = response.data.import_log_id;
                this.pollImportStatus(importLogId);
            })
            .catch(error => {
                this.error = error.response?.data?.message || 'An error occurred while uploading the file.';
                this.uploading = false;
            })
            .finally(() => {
                // Reset file input
                event.target.value = null;
            });
        },

        pollImportStatus(importLogId, attempt = 1) {
            const maxAttempts = 60; // 5 minutes with 5 second intervals
            
            if (attempt > maxAttempts) {
                this.error = 'Import is taking longer than expected. Please check back later.';
                this.uploading = false;
                return;
            }
            
            axios.get(`/sites/${this.siteId}/import/${importLogId}/status`, {
                headers: {
                    'X-Requested-With': 'XMLHttpRequest'
                }
            })
            .then(response => {
                const { status, imported_count, failed_count, total_rows, errors } = response.data;
                
                if (status === 'completed') {
                    console.log('Import completed:', response.data);
                    this.success = `Successfully imported ${imported_count} ${this.importType}.`;
                    if (failed_count > 0) {
                        this.error = `Failed to import ${failed_count} ${this.importType}.`;
                    }
                    this.uploading = false;
                    this.$emit('imported', this.customers);
                } 
                else if (status === 'failed') {
                    console.log('Import failed:', response.data);
                    this.error = 'Import failed. ' + (errors?.[0]?.message || 'Please check the file and try again.');
                    this.uploading = false;
                }
                else {
                    // Still processing, poll again after a delay
                    setTimeout(() => {
                        this.pollImportStatus(importLogId, attempt + 1);
                    }, 5000); // Poll every 5 seconds
                    
                    // Update status message
                    this.success = `Processing... (${imported_count + failed_count}/${total_rows || '?'} records processed)`;
                }
            })
            .catch(error => {
                console.error('Error checking import status:', error);
                // Retry on error
                if (attempt < maxAttempts) {
                    setTimeout(() => {
                        this.pollImportStatus(importLogId, attempt + 1);
                    }, 5000);
                } else {
                    this.error = 'Unable to check import status. Please refresh the page to check if the import completed.';
                    this.uploading = false;
                }
            });
        },

        loadCustomers() {
            axios.get(`/sites/${this.siteId}/customers`)
                .then(response => {
                    this.customers = response.data;
                    // Update customers count badge
                    const customersTab = this.tabs.find(tab => tab.id === 'customers');
                    if (customersTab) {
                        customersTab.badge = this.customers.length;
                    }
                })
                .catch(error => {
                    console.error('Error loading customers:', error);
                });
        },

        async downloadSampleCsv() {
            try {
                // Get the current import type from the active tab
                const importType = this.importType;
                
                // Show loading state
                this.uploading = false;
                
                // Make API request to get the file
                const response = await axios({
                    url: `/sample-${importType}-import`,
                    method: 'GET',
                    responseType: 'blob', // Important for binary data
                });
                
                // Create a URL for the blob
                const url = window.URL.createObjectURL(new Blob([response.data]));
                
                // Create a temporary link and trigger download
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `sample-${importType}-import.xlsx`);
                document.body.appendChild(link);
                link.click();
                
                // Clean up
                window.URL.revokeObjectURL(url);
                document.body.removeChild(link);
                
            } catch (error) {
                console.error('Error downloading sample file:', error);
                this.error = 'Failed to download sample file. Please try again.';
            } finally {
                this.uploading = false;
            }
        },

       formatDate(dateString) {
            if (!dateString) return '';
            const options = { 
                year: 'numeric', 
                month: 'short', 
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            };
            return new Date(dateString).toLocaleDateString(undefined, options);
        },

        getStatusBadgeClass(status) {
            const statusClasses = {
                'pending': 'bg-yellow-100 text-yellow-800',
                'processing': 'bg-blue-100 text-blue-800',
                'completed': 'bg-green-100 text-green-800',
                'failed': 'bg-red-100 text-red-800'
            };
            return statusClasses[status?.toLowerCase()] || 'bg-gray-100 text-gray-800';
        },

        downloadErrorLog(importLogId) {
            console.log('Downloading error log for import:', importLogId);
            window.location.href = `/sites/${this.siteId}/import/${importLogId}/errors`;
        }
    },
    
    watch: {
        activeTab: {
            immediate: true,
            handler(newTab) {
                console.log('Tab changed to:', newTab);
                this.loadTabData(newTab);
            }
        }
    },

    created() {
        console.log('Component created, loading initial tab:', this.activeTab);
        // Load initial data
        this.loadTabData(this.activeTab);
        // Load data for the initially active tab
        if (this.activeTab && this.pagination[this.activeTab]) {
            this.loadTabData(this.activeTab);
        }
    },
};
</script>
